import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Brand } from "./Brand";
import { Button } from "./ui/button";
import { CheckCircle2, Loader2, AlertOctagon, Home as HomeIcon, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * /return-from-xgate — the post-payment landing for SEPA Flow B.
 *
 * ⚠️  SCAFFOLDING / STOPGAP — pin to XGate's documented contract once received.
 *     This component MUST be replaced with the canonical XGate param shape the
 *     moment we have their docs. The defensive multi-shape parser below is a
 *     best-effort to stop 404'ing customers TODAY; it is NOT the final design.
 *
 * 🔒 SAFETY INVARIANT:
 *     This route NEVER sets or changes order state from URL params. URL params
 *     are user-mutable (a customer can edit ?status=success in the address
 *     bar). The redirect ONLY navigates the customer to the status page —
 *     authoritative state always comes from XGate's signed inbound callback
 *     (`POST /api/xgate/callback`). Return URL = navigation, NOT truth.
 *
 * The route accepts a wide set of param names so it works regardless of which
 * XGate picks: order_id / orderId / merchant_reference / reference /
 * external_id, and status / state / outcome. Unknown params are surfaced in
 * the `?debug=1` panel so we can lock in the contract during the live
 * round-trip.
 *
 * Behaviour:
 *  1. Extract the order id (any of the accepted aliases)
 *  2. READ-ONLY confirm against our backend (`GET /v1/orders/:id`) that the
 *     order exists and is associated with this customer
 *  3. Redirect to the existing `/widget/status/:id` page (which polls the
 *     authoritative state)
 *
 * If we can't resolve an order id at all, show a friendly fallback page that
 * routes to the customer's order list. Never a 404.
 */
const ID_KEYS    = ["order_id", "orderId", "id", "merchant_reference", "merchantReference", "reference", "external_id", "externalId"];
const STATUS_KEYS = ["status", "state", "outcome", "result"];

function pickFirst(params, keys) {
  for (const k of keys) {
    const v = params.get(k);
    if (v) return v;
  }
  return null;
}

export default function ReturnFromXgate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth() || {};
  const [state, setState] = useState("resolving"); // resolving | found | not_found | error
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  const incomingId = useMemo(() => pickFirst(params, ID_KEYS), [params]);
  const incomingStatus = useMemo(() => pickFirst(params, STATUS_KEYS), [params]);
  const debugParams = useMemo(() => {
    const out = {};
    for (const [k, v] of params.entries()) out[k] = v;
    return out;
  }, [params]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!incomingId) {
        setState("not_found");
        return;
      }
      try {
        const r = await api.get(`/v1/orders/${incomingId}`);
        if (cancelled) return;
        setOrder(r.data?.order || r.data);
        setState("found");
        // Auto-redirect to the existing status page after a beat so the
        // customer sees the success reassurance first.
        setTimeout(() => {
          if (!cancelled) navigate(`/widget/status/${incomingId}`, { replace: true });
        }, 1800);
      } catch (e) {
        if (cancelled) return;
        // Order might exist but not be on this customer (XGate redirect on a
        // different browser session). Don't 404 — route them to their orders
        // list so they can find it themselves.
        setError(e?.response?.data?.detail || String(e));
        setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [incomingId, navigate]);

  return (
    <div className="xch-app-bg min-h-screen pb-10">
      <header className="xch-ink sticky top-0 z-20">
        <div className="max-w-md mx-auto px-4 py-3">
          <Brand size={28} theme="dark" />
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 pt-10">
        <div className="xch-card p-6 md:p-8 space-y-5 text-center" data-testid="return-xgate-card">
          {state === "resolving" && (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin" />
              <h1 className="font-display text-2xl font-bold text-foreground">Welcome back</h1>
              <p className="text-sm text-muted-foreground">Confirming your payment with XGate…</p>
            </>
          )}

          {state === "found" && (
            <>
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-700 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h1 className="font-display text-2xl font-bold text-foreground">Welcome back</h1>
              <p className="text-sm text-muted-foreground">
                Thanks — XGate has handed your payment back to us. Sending you to your order status…
              </p>
              <Button
                onClick={() => navigate(`/widget/status/${incomingId}`, { replace: true })}
                className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                data-testid="return-xgate-go-status-btn"
              >
                See order status <ArrowRight className="w-4 h-4" />
              </Button>
              {incomingStatus && (
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  XGate reported: <strong className="text-foreground">{incomingStatus}</strong>
                </div>
              )}
            </>
          )}

          {state === "not_found" && (
            <>
              <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-700 mx-auto flex items-center justify-center">
                <AlertOctagon className="w-6 h-6" />
              </div>
              <h1 className="font-display text-2xl font-bold text-foreground">We couldn't link that order</h1>
              <p className="text-sm text-muted-foreground">
                XGate sent you back to us but didn't include an order reference we recognise. Don't worry — your payment is safe with XGate. Open your order history to find the order you just paid.
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => navigate(user ? "/account/orders" : "/login")}
                  className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                  data-testid="return-xgate-orders-btn"
                >
                  Open my orders <ArrowRight className="w-4 h-4" />
                </Button>
                <Button onClick={() => navigate("/")} variant="ghost" className="rounded-xl gap-1.5">
                  <HomeIcon className="w-4 h-4" /> Back to home
                </Button>
              </div>
            </>
          )}

          {state === "error" && (
            <>
              <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-700 mx-auto flex items-center justify-center">
                <AlertOctagon className="w-6 h-6" />
              </div>
              <h1 className="font-display text-2xl font-bold text-foreground">Welcome back</h1>
              <p className="text-sm text-muted-foreground">
                We received the return from XGate but couldn't load the order on this device. This usually happens when you signed in on a different browser. Sign in here and we'll show your order status.
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => navigate(`/login?next=/widget/status/${incomingId}`)}
                  className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                  data-testid="return-xgate-login-btn"
                >
                  Sign in to see status <ArrowRight className="w-4 h-4" />
                </Button>
                <Button onClick={() => navigate("/")} variant="ghost" className="rounded-xl gap-1.5">
                  <HomeIcon className="w-4 h-4" /> Back to home
                </Button>
              </div>
              {error && (
                <div className="text-[10px] text-muted-foreground font-mono break-all">
                  ref: {incomingId} · {error}
                </div>
              )}
            </>
          )}

          {/* Debug — only visible to admins via ?debug=1, helps us nail XGate's actual param contract */}
          {params.get("debug") === "1" && (
            <details className="text-left text-[11px]" data-testid="return-xgate-debug">
              <summary className="cursor-pointer text-muted-foreground">Inbound params (debug)</summary>
              <pre className="mt-2 bg-muted/30 rounded-lg p-2 overflow-x-auto">
                {JSON.stringify(debugParams, null, 2)}
              </pre>
            </details>
          )}
        </div>

        <p className="text-center text-[11px] text-muted-foreground mt-4">
          Xchangely.io
        </p>
      </div>
    </div>
  );
}
